import React, { useState, useCallback, useEffect } from "react";
import PropTypes from "prop-types";
import TextField from "@mui/material/TextField";
import { useSelector, useDispatch } from "react-redux";
import dayjs from 'dayjs';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import HighlightOffOutlinedIcon from '@mui/icons-material/HighlightOffOutlined';
import {getMetadataDefinitions} from "../../actions/metadataDefinitionActions";
import ArgoTextFieldHook from "./ArgoTextFieldHook";
import ArgoTextFieldSelectHook from "./ArgoTextFieldSelectHook";
import ArgoAutocompleteHook from "./ArgoAutocompleteHook";
import ArgoSectionOpenCloseHook from "./ArgoSectionOpenCloseHook";
import ArgoIconButtonHook from "./ArgoIconButtonHook";
import ArgoButtonHook from "./ArgoButtonHook";
import * as metadataUtils from "../../utilities/metadata";
import * as metadataTemplateTypes from "../../constants/metadataTemplateTypes";
import {compressImage, imageDataUri} from "../../utilities/imageCompression";
import {notificationOpen} from "../../actions/notificationActions";

const inputTypes = {
    FREETEXT: "FREETEXT",
    BOOLEAN: "BOOLEAN",
    INTEGER: "INTEGER",
    DECIMAL: "DECIMAL",
    DATE: "DATE",
    JSON: "JSON",
    LIST: "LIST"
};

const ArgoMetadataEditor = (props) => {
    const {
        entityType,
        template,
        depth,
        metadata,
        setMetadataErrorText,
        questionNumber,
        hidden
    } = props;

    const dispatch = useDispatch();
    
    // Get Redux state
    const metadataDefinition = useSelector(state => state.metadataDefinition[entityType]);
    const settings = useSelector(state => state.settings);

    const [state, setState] = useState({
        sectionMetadataOpen: false,
        nameErrorTextArray: [],
        valueErrorTextArray: [],
        metadataEntries: Object.entries(metadata),
        currentEntryIndex: 0,
        base64TextString: "",
        bypassComponentWillReceiveProps: false
    });

    useEffect(() => {
        dispatch(getMetadataDefinitions(entityType));
    }, [dispatch, entityType]);

    useEffect(() => {
        if (state.bypassComponentWillReceiveProps) {
            return;
        }

        let entries = Object.entries(metadata);
        let nameErrorTextArray = [];
        let valueErrorTextArray = [];
        
        for (let i = 0; i < entries.length; i++) {
            nameErrorTextArray.push(" ");
            valueErrorTextArray.push(" ");
        }

        setState(prevState => ({
            ...prevState,
            metadataEntries: entries,
            nameErrorTextArray: nameErrorTextArray,
            valueErrorTextArray: valueErrorTextArray
        }));
    }, [metadata, state.bypassComponentWillReceiveProps]);

    // const fromEntries = useCallback((entries) => {
    //     return [...entries].reduce((obj, {0: key, 1: val}) => Object.assign(obj, {[key]: val}), {});
    // }, []);

    const getMetadataDefinition = useCallback((name) => {
        return metadataDefinition.find(def => def.name === name);
    }, [metadataDefinition]);

    const getMetadataDefinitionInputType = useCallback((name) => {
        let metadataDefinitionItem = getMetadataDefinition(name);
        return metadataDefinitionItem !== undefined ? metadataDefinitionItem.type : inputTypes.FREETEXT;
    }, [getMetadataDefinition]);

    const validate = useCallback((entries) => {
        let hasDuplicate = false;
        let isNumber = false;
        let hasInvalidChars = false;
        let hasBlankName = false;
        let nameErrorTextArray = [];

        for (let i = 0; i < entries.length; i++) {
            nameErrorTextArray[i] = " ";
        }

        let keys = [];
        for (let i = 0; i < entries.length; i++) {
            keys.push(entries[i][0]);
        }
        let saveIndex = null;

        for (let i = entries.length-1; i >= 0; i--) {
            let matchIndex = keys.indexOf(entries[i][0]);
            if (matchIndex >= 0) {
                if (matchIndex !== i) {
                    hasDuplicate = true;
                    saveIndex = i;
                }
            }
        }

        if (hasDuplicate) {
            nameErrorTextArray[saveIndex] = "Metadata property names must not be duplicates";
        }
        else {
            saveIndex = null;
            for (let i = entries.length-1; i >= 0; i--) {
                if (!isNaN(entries[i][0]) && entries[i][0].trim() !== "") {
                    isNumber = true;
                    saveIndex = i;
                    break;
                }
            }

            if (isNumber) {
                nameErrorTextArray[saveIndex] = "Metadata property names cannot be a number";
            }
            else {
                saveIndex = null;
                for (let i = entries.length-1; i >= 0; i--) {
                    console.log(entries[i][0])
                    if (entries[i][0].match(/^([a-zA-Z0-9._-])+$/) === null &&  entries[i][0] !== "") {
                        hasInvalidChars = true;
                        saveIndex = i;
                        break;
                    }
                }

                if (hasInvalidChars) {
                    nameErrorTextArray[saveIndex] = "Metadata property name contains invalid character(s)"
                }
                else {
                    saveIndex = null;
                    for (let i = entries.length-1; i >= 0; i--) {
                        if (entries[i][0].trim() === "") {
                            hasBlankName = true;
                            saveIndex = i;
                            break;
                        }
                    }

                    if (hasBlankName) {
                        nameErrorTextArray[saveIndex] = "Metadata property names cannot be blank";
                    }
                }
            }
        }

        return nameErrorTextArray;
    }, []);

    // const hasError = useCallback(() => {
    //     let hasErrorFlag = state.nameErrorTextArray.length === 0 || (state.nameErrorTextArray.find((str) => str !== " ") !== undefined);
    //
    //     if (!hasErrorFlag) {
    //         let metadataObj = fromEntries(state.metadataEntries);
    //
    //         let updatedTemplate = {...template};
    //         updatedTemplate.metadata = metadataObj;
    //         onChangeCallback(updatedTemplate, depth);
    //     }
    // }, [state.nameErrorTextArray, state.metadataEntries, fromEntries, template, onChangeCallback, depth]);

    const setMetadataErrorTextCallback = useCallback((nameErrorTextArray) => {
        let joinedNameErrorText = nameErrorTextArray.filter(error => error !== " ").join(", ");
        if (setMetadataErrorText) {
            setMetadataErrorText(joinedNameErrorText);
        }
    }, [setMetadataErrorText]);

    const handleValidateAndUpdate = useCallback((metadataEntries) => {
        let nameErrorTextArray = validate(metadataEntries);
        setState(prevState => ({...prevState, metadataEntries: metadataEntries, nameErrorTextArray: nameErrorTextArray}));
        setMetadataErrorTextCallback(nameErrorTextArray);
    }, [validate, setMetadataErrorTextCallback]);

    const onClickToggleSectionOpenClose = useCallback((event, fieldName) => {
        setState(prevState => ({...prevState, [fieldName]: !prevState[fieldName]}));
    }, []);

    const onSelectChange = useCallback((event, fieldName, menuItemText, index) => {
        handleOnChange(fieldName, index, event, menuItemText);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleOnChange = useCallback((field, index, event, value) => {
        let entry = state.metadataEntries[index];
        const {0: prevKey, 1: prevValue} = entry;
        let updatedState = {};

        let metadataEntries = [...state.metadataEntries];

        if (field === "name") {
            let newInputType  = getMetadataDefinitionInputType(value);
            let prevInputType = getMetadataDefinitionInputType(prevKey);

            if (newInputType === prevInputType) {
                metadataEntries[index] = [value, prevValue]
            } else {
                metadataEntries[index] = [value, ""];
            }
        } else {
            metadataEntries[index] = [prevKey, value];
        }

        updatedState.metadataEntries = metadataEntries;
        updatedState.nameErrorTextArray = state.nameErrorTextArray;

        handleValidateAndUpdate(metadataEntries);

        setState(prevState => ({...prevState, ...updatedState}));
    }, [state.metadataEntries, state.nameErrorTextArray, getMetadataDefinitionInputType, handleValidateAndUpdate]);

    const onTextChange = useCallback((event, fieldName, index) => {
        let value = event.target.value;
        let nameErrorTextArray = state.nameErrorTextArray;
        let metadataEntries = state.metadataEntries;

        if (fieldName === "name") {
            if (metadataEntries[index][0] !== value) {
                metadataEntries[index][1] = "";
            }

            metadataEntries[index][0] = value;
            nameErrorTextArray = validate(metadataEntries);
        }
        else {
            let preDefinedName  = getMetadataDefinition(metadataEntries[index][0]);
            let preDefinedNameType = inputTypes.FREETEXT;
            if (preDefinedName !== undefined) {
                preDefinedNameType = getMetadataDefinitionInputType(metadataEntries[index][0]);
            }

            if (preDefinedNameType === inputTypes.DECIMAL) {
                if (value.match(/^-?\d+\.?\d*$/) || value === "") {
                    metadataEntries[index][1] = value;
                }
            }
            else {
                metadataEntries[index][1] = value;
            }
        }

        setState(prevState => ({...prevState, nameErrorTextArray: nameErrorTextArray, metadataEntries: metadataEntries}));
        handleOnChange(fieldName, index, event, value);
    }, [state.nameErrorTextArray, state.metadataEntries, validate, getMetadataDefinition, getMetadataDefinitionInputType, handleOnChange]);

    const onImageUploadChange = useCallback((e, entryIndex) => {
        let file = e.target.files[0];

        if (file) {
            compressImage(file).then((dataUri) => {
                setState(prevState => ({...prevState, base64TextString: dataUri, currentEntryIndex: entryIndex}));
                handleOnChange("value", entryIndex, null, dataUri);
            }).catch((err) => {
                dispatch(notificationOpen(err.message));
                e.target.value = "";
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [handleOnChange]);

    const addProperty = useCallback(() => {
        let metadataEntries = [...state.metadataEntries, ["", ""]];
        setState(prevState => ({...prevState, bypassComponentWillReceiveProps: true}));
        handleValidateAndUpdate(metadataEntries);
    }, [state.metadataEntries, handleValidateAndUpdate]);

    const onClickRemoveMetadata = useCallback((event, fieldName, index) => {
        let filteredEntries = state.metadataEntries.filter((entry, entryIndex) => entryIndex !== index);
        handleValidateAndUpdate(filteredEntries);
        setState(prevState => ({...prevState, metadataEntries: filteredEntries}));
    }, [state.metadataEntries, handleValidateAndUpdate]);

    const onChangeDateField = useCallback((fieldName, index, value) => {
        let metadataEntries = state.metadataEntries;
        let formattedValue = dayjs(value).format("YYYY-MM-DD");

        if (index !== undefined) {
            metadataEntries[index][1] = formattedValue;
        }
        else {
            metadataEntries[metadataEntries.length-1][1] = formattedValue;
        }

        setState(prevState => ({...prevState, metadataEntries: metadataEntries}));
    }, [state.metadataEntries]);

    const renderValueField = useCallback((name, value, index) => {
        let html;

        let type = inputTypes.FREETEXT;
        let listValues = [];

        let metadataDefinitionItem = getMetadataDefinition(name);

        if (metadataDefinitionItem !== undefined) {
            type = metadataDefinitionItem.type;
            listValues = metadataDefinitionItem.listValues;
        }

        if (name.startsWith("image-") && ((entityType === metadataTemplateTypes.CategoryTemplate) || (entityType === metadataTemplateTypes.QuestionnaireTemplate))) {

            html = <div id="ArgoMetadataEditor-MetadataValue-container-div" style={{display: "flex", width: "350px"}}>
                <div style={{alignSelf: "center"}}>
                    <div style={{color: "gray", fontSize: "12px"}}>
                        Images are auto-compressed to fit
                    </div>
                    <form onChange={(e) => onImageUploadChange(e, index)}>
                        <input
                            type="file"
                            name="image"
                            id="file"
                            accept=".jpeg, .png, .jpg, .webp, .svg"
                        />
                    </form>
                    <ArgoTextFieldHook
                        fileName={"ArgoMetadataEditor"}
                        fieldName="value"
                        label="Value"
                        value={template.value}
                        width="300px"
                        multiLine={true}
                        onChange={onTextChange}
                        index={questionNumber}
                    />
                    {value ?
                        <div style={{width: '40%', textAlign: "center", alignSelf: "center"}}>
                            <img src={imageDataUri(value)} alt="" style={{verticalAlign: 'middle', height: '60px'}}/>
                        </div> : null
                    }
                </div>
            </div>
        }
        else if (type === inputTypes.DATE) {

            html = <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DesktopDatePicker
                    id="ArgoMetadataEditor-date"
                    fullWidth
                    value={value}
                    onChange={e=>onChangeDateField("value", index, e["$d"])}
                    label="Value"
                    container="inline"
                    autoFocus
                    renderInput={(params) => <TextField {...params}  variant="standard" error={false} sx={{width: "350px", marginBottom: "22px"}}/>}
                />
            </LocalizationProvider>
        }
        else if ((type === inputTypes.BOOLEAN) || (type === inputTypes.LIST))  {

            if (type === inputTypes.BOOLEAN) {
                listValues = ["false", "true"];
            }

            let listIndex = listValues.indexOf(value);

            html = <ArgoTextFieldSelectHook
                fileName="ArgoMetadataEditor"
                fieldName={(type === inputTypes.BOOLEAN) ? "type-boolean" : "type-list"}
                label="Value"
                value={listIndex}
                width="350px"
                onChange={onSelectChange}
                menuItems={listValues}
                index={index}
            />
        }
        else {

            let placeholder = (type === inputTypes.INTEGER) ? "Enter an integer." : (type === inputTypes.INTEGER ? "Enter a decimal." : "");

            html = <ArgoTextFieldHook
                fileName={"ArgoMetadataEditor-" + type.toLowerCase()}
                fieldName="value"
                label="Value"
                value={value}
                placeholder={placeholder}
                type={type === inputTypes.INTEGER ? "number" : "text"}
                multiLine
                autoFocus
                onChange={onTextChange}
                width="350px"
                maxLength={4095}
                errorText={state.valueErrorTextArray.length > 0 ? state.valueErrorTextArray[index] : " "}
                index={index}
                validationType={type}
                divContainerHeight="70px"
            />

        }

        return html;
    }, [getMetadataDefinition, entityType, template, questionNumber, onImageUploadChange, onTextChange, onChangeDateField, onSelectChange, state.valueErrorTextArray]);

    return (hidden && settings.hideMetadataProperties) ? "" : <div>
            <ArgoSectionOpenCloseHook
                fileName="ArgoMetadataEditor"
                fieldName="sectionMetadataOpen"
                sectionTitle={"Metadata Properties: (" + metadataUtils.countNumberOfVisibleMetadataEntries(template.metadata) + ")"}
                onClick={onClickToggleSectionOpenClose}
                open={state.sectionMetadataOpen}
            />

            {state.sectionMetadataOpen && <div>
                {state.metadataEntries.map(({0: name, 1: value}, index) => {
                    return (
                        (metadataUtils.isHiddenMetadata(name)) ? "" :
                            <div key={index}>
                                <div id={"ArgoMetadataEditor-metadata-line-container-div" + index} style={{display: "flex", flexDirection: "row", alignItems: "center", marginLeft: "32px", width: "100%", height: "52px"}}>

                                    <ArgoAutocompleteHook
                                        fileName={"ArgoMetadataEditor-" + questionNumber + "-" + index}
                                        fieldName="name"
                                        index={index}
                                        label="Name"
                                        value={name}
                                        onChange={onTextChange}
                                        menuItems={metadataDefinition.map(d=>d.name)}
                                        maxLength="255"
                                        multiline={true}
                                        openOnFocus
                                        errorText={state.nameErrorTextArray.length > 0 ? state.nameErrorTextArray[index] : " "}
                                        width="350px"
                                    />

                                    <div style={{width: "20px"}}>&nbsp;</div>

                                    <div id="ArgoMetadataEditor-MetadataValue-container-div" style={{display: "flex", width: "350px"}}>
                                        {renderValueField(name, value, index)}
                                    </div>

                                    <ArgoIconButtonHook
                                        fileName="ArgoMetadataEditor"
                                        fieldName="remove-metadtata"
                                        index={index}
                                        depth={depth}
                                        onClick={onClickRemoveMetadata}
                                        icon={<HighlightOffOutlinedIcon/>}
                                    />

                                </div>
                                <div>&nbsp;</div>
                            </div>

                    );
                })
                }

                <div>&nbsp;</div>

                <div style={{paddingLeft: "30px"}}>
                    <ArgoButtonHook
                        fileName="ArgoMetadataEditor"
                        fieldName="AddProperty"
                        label="Add a Property"
                        onClick={addProperty}
                        width={160}
                        disabled={state.nameErrorTextArray.find((str) => str !== " ") !== undefined}
                    />
                </div>

            </div>
            }
        </div>
};

ArgoMetadataEditor.defaultProps = {
    metadata: {},
    errorText: " ",
    questionNumber: 0,
    onChangeCallback: function () {},
    hidden: false
};

ArgoMetadataEditor.propTypes = {
    entityType: PropTypes.string,
    template: PropTypes.object,
    onChange: PropTypes.func,
    onChangeCallback: PropTypes.func,
    depth: PropTypes.number,
    metadata: PropTypes.object,
    setMetadataErrorText:  PropTypes.func,
    questionNumber: PropTypes.number,
    hidden: PropTypes.bool
};

export default ArgoMetadataEditor;
